import type { ContractHandlers } from '@cognitoforms/builder-types';
import type { LegacyForm, LegacyHandlers } from '../legacy-mirrored-builder';
import type { LookupFormSettings } from '@cognitoforms/types/server-types/core/model/forms/definition/lookup-form-settings';
import type { PeopleFormSettings } from '@cognitoforms/types/server-types/forms/model/people-form-settings';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Cognito: any;

type SettingsHandlers = Pick<LegacyHandlers, 'updateFormSettings' | 'updatePaymentSettings' | 'updateWorkflow'>;
type UpdateFormSettingsArgs = Parameters<ContractHandlers['updateFormSettings']>[0];

const applyPeopleForm = (legacyForm: LegacyForm, peopleForm: Partial<PeopleFormSettings>) => {
	const settings = legacyForm.get_PeopleFormSettings() ?? Cognito.deserialize(Cognito.Forms.PeopleFormSettings, {});

	if ('PersonType' in peopleForm)
		settings.set_PersonType(Cognito.getEnumWithName(Cognito.Forms.PersonFormType, peopleForm.PersonType));
	if ('Name' in peopleForm) settings.set_Name(peopleForm.Name ?? null);
	if ('Email' in peopleForm) settings.set_Email(peopleForm.Email ?? null);
	if ('Phone' in peopleForm) settings.set_Phone(peopleForm.Phone ?? null);
	if ('Address' in peopleForm) settings.set_Address(peopleForm.Address ?? null);
	if ('Avatar' in peopleForm) settings.set_Avatar(peopleForm.Avatar ?? null);
	if ('IsActive' in peopleForm) settings.set_IsActive(peopleForm.IsActive ?? null);
	if ('IsGuestList' in peopleForm) settings.set_IsGuestList(peopleForm.IsGuestList);
	if ('InviteUsers' in peopleForm) settings.set_InviteUsers(peopleForm.InviteUsers);
	if ('GuestRegistration' in peopleForm) settings.set_GuestRegistration(peopleForm.GuestRegistration);
	if ('GuestProfileEditing' in peopleForm) settings.set_GuestProfileEditing(peopleForm.GuestProfileEditing);

	legacyForm.set_PeopleFormSettings(settings);
};

// The new model nests this under Settings.LookupForm; the legacy form exposes it as a flat
// property (form.get/set_SecureLookupsForGuests), same as identify-submitter.ts's usage.
const applyLookupForm = (legacyForm: LegacyForm, lookupForm: Partial<LookupFormSettings>) => {
	if ('PreventLookup' in lookupForm)
		legacyForm.set_PreventLookup(lookupForm.PreventLookup);
	if ('SecureLookupsForGuests' in lookupForm)
		legacyForm.set_SecureLookupsForGuests(lookupForm.SecureLookupsForGuests);
	if ('SecureSettingsChoiceDialogShown' in lookupForm)
		legacyForm.set_SecureSettingsChoiceDialogShown(lookupForm.SecureSettingsChoiceDialogShown);
	if ('HasLookups' in lookupForm)
		legacyForm.set_HasLookups(lookupForm.HasLookups);
	if ('HasV2Lookups' in lookupForm)
		legacyForm.set_HasV2Lookups(lookupForm.HasV2Lookups);
	if (lookupForm.EntrySummary !== undefined) {
		legacyForm.set_Format(lookupForm.EntrySummary);
		if (lookupForm.EntrySummaryCustomized === undefined)
			legacyForm.set_EntrySummaryCustomized(lookupForm.EntrySummary !== null);
	}
	if (lookupForm.EntrySummaryCustomized !== undefined)
		legacyForm.set_EntrySummaryCustomized(lookupForm.EntrySummaryCustomized);
};

export const createLegacySettingsHandlers = (): SettingsHandlers => ({
	updateFormSettings: (legacyForm: LegacyForm, args: UpdateFormSettingsArgs) => {
		if ('Name' in args)
			legacyForm.set_Name(args.Name);
		if ('Title' in args)
			legacyForm.set_Title(args.Title);
		if ('UrlName' in args)
			legacyForm.set_UrlName(args.UrlName);
		if ('Description' in args)
			legacyForm.set_Description(args.Description);
		if ('FormType' in args)
			legacyForm.set_FormType(Cognito.getEnumWithName(Cognito.Forms.FormType, args.FormType));
		if (args.PeopleForm)
			applyPeopleForm(legacyForm, args.PeopleForm);
		if (args.LookupForm)
			applyLookupForm(legacyForm, args.LookupForm);

		legacyForm.set_HasChanges(true);
	}

	// updatePaymentSettings, updateWorkflow: add as those areas move to the new model.
});
